import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { coupleAuthMeCoupleAuthPost } from "../../__generated__/api/me/me";
import type { CoupleAuthOut } from "../../__generated__/api/wrenAPI.schemas";
import type { CustomRequestInit } from "../../api/client";
import { useMeAuth } from "./MeAuthContext";
import { stashShareLinkToken } from "./meToken";
import { markInviteOpenPending } from "../analytics/track";

/**
 * /me shared-link landing — redeems the couple-anchored `?token=` for a
 * COUPLE-scoped session via `POST /me/couple/auth` (WP-A) and stores it under
 * the generic `client_session_token` key (via MeAuthContext), then drops the
 * opener into the flow. The couple token binds no person: the opener reads the
 * roster (`/me/couple/preview`) and self-selects at S2, which mints the
 * person-bound token.
 *
 * The opaque `?token=` is ALSO stashed (couple-anchored, re-openable by design)
 * so S11 can reconstruct the same shared link to invite the second partner.
 *
 * This is the /me sibling of the homebase `AuthPage`; it does NOT touch the
 * homebase `guided_chat_token` key.
 */
export function MeAuthLanding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, logout } = useMeAuth();
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get("token");
  const isLogout = searchParams.has("logout");
  const isExpired = searchParams.has("expired");
  // Copy-split: a cold, link-less arrival (the auth gate's `?nosession`) is NOT
  // an expiry — it gets neutral copy. "Session expired" (`?expired`) is reserved
  // for a previously-valid session that timed out (the 401 interceptor).
  const isNoSession = searchParams.has("nosession");

  useEffect(() => {
    if (isLogout) {
      logout();
      navigate("/me", { replace: true });
      return;
    }

    if (token) {
      setError(null);
      // skip401Redirect: an invalid link must not clear a valid session nor
      // bounce us to the global handler.
      coupleAuthMeCoupleAuthPost(
        { token },
        { skip401Redirect: true } as CustomRequestInit
      )
        .then((response) => {
          const data = response.data as CoupleAuthOut;
          // Stash the opaque link token BEFORE the session token so a re-render
          // races can't drop it: it's the re-openable invite link for S11.
          stashShareLinkToken(token);
          setToken(data.token);
          // Queue the open rather than firing it here: couple_id/therapist_id
          // aren't registered as super properties until MeShell loads the
          // couple, so capturing before then would drop the event's join
          // keys. useMeAnalyticsIdentity fires it once those are set.
          markInviteOpenPending();
          navigate("/me", { replace: true });
        })
        .catch(() => setError("Invalid or expired link"));
      return;
    }

    if (isExpired) {
      setError("Your session has expired. Please request a new link.");
      return;
    }

    if (isNoSession) {
      setError("Open the link your therapist sent to continue.");
      return;
    }

    // No params → straight into the flow (which gates on auth).
    navigate("/me", { replace: true });
  }, [token, isLogout, isExpired, isNoSession, logout, setToken, navigate]);

  if (error) {
    // Three distinct arrivals share this surface (copy-split):
    //   • `?expired`   → a previously-valid session timed out → "Session expired"
    //   • `?nosession` → a cold, link-less/no-session visit   → neutral prompt
    //   • token catch  → a bad/expired invite link            → "Link expired"
    const heading = isExpired
      ? "Session expired"
      : isNoSession
        ? "Open your session link"
        : "Link expired";
    const body = isNoSession
      ? "Open the link your therapist sent, or text Dova for a fresh login link."
      : "To get a new link, reach out to your therapist or text Dova for a fresh login link.";
    return (
      // Cardless, so this IS the page: --rail-dark like the ground the card
      // sits on, with the on-dark ink ramp. It was --muted with the LIGHT ramp,
      // which is the recede grey the rest of the flow moved off, and it is the
      // screen a client meets when their link has died — the last place to look
      // like an unstyled fallback.
      <div className="min-h-dvh flex items-center justify-center bg-[var(--rail-dark)] px-4">
        <div className="max-w-md w-full text-center">
          <h1 className="font-serif text-2xl font-medium text-[var(--text-on-dark)] mb-4">
            {heading}
          </h1>
          <p className="text-base leading-[1.5] text-[var(--text-on-dark-secondary)]">
            {body}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-[var(--rail-dark)]">
      <p className="text-base text-[var(--text-on-dark-secondary)]">Verifying…</p>
    </div>
  );
}
